/**
 * Conveyance view — F13A (go live / agent handoff).
 * Generates the sovereign book package: isolated corpus + register slice + endpoint.
 */

import { useState } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

export function ConveyanceView({ isOperator }: { isOperator: boolean }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    const clients = getClients();
    if (!clients) return;
    setBusy(true);
    setError(null);
    try {
      const res: any = await clients.control.generateConveyance({
        tenant_id: "self",
        package_type: "sovereign_book",
      });
      setResult(applyMembrane(res, isOperator));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("F13A", isOperator)}</span>
        <h1>{applyMembrane("Conveyance", isOperator)}</h1>
      </div>
      <div className="card">
        <div className="card-title">{applyMembrane("Your Agent Handoff", isOperator)}</div>
        <p className="card-desc">
          {applyMembrane(
            "Generate your sovereign book — an isolated package containing your agent's corpus, register slice, and live endpoint. This is your business, conveyed.",
            isOperator
          )}
        </p>
        <button className="btn-primary" onClick={generate} disabled={busy}>
          {busy ? "Generating…" : applyMembrane("Generate Conveyance Package", isOperator)}
        </button>
      </div>
      {error && <div className="error-state">{error}</div>}
      {result && (
        <div className="card">
          <div className="card-title">Package Generated</div>
          <div className="object-fields">
            {result.register_snapshot && (
              <>
                <div className="object-field">
                  <span className="object-field-label">Receipts</span>
                  <span className="object-field-value">{result.register_snapshot.receipt_count ?? "—"}</span>
                </div>
                <div className="object-field">
                  <span className="object-field-label">Coverage</span>
                  <span className="object-field-value">{result.register_snapshot.coverage ?? "—"}%</span>
                </div>
                <div className="object-field">
                  <span className="object-field-label">Objects</span>
                  <span className="object-field-value">{result.register_snapshot.object_count ?? "—"}</span>
                </div>
              </>
            )}
            {result.endpoint && (
              <div className="object-field">
                <span className="object-field-label">Endpoint</span>
                <span className="object-field-value"><code className="mono">{result.endpoint}</code></span>
              </div>
            )}
            {result.generated_at && (
              <div className="object-field">
                <span className="object-field-label">Generated</span>
                <span className="object-field-value">{result.generated_at}</span>
              </div>
            )}
          </div>

          {/* Download button — client-side JSON blob */}
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn-secondary"
              onClick={() => {
                const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `rida-conveyance-${Date.now()}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download JSON
            </button>

            {/* Verify button — calls the endpoint and confirms it returns data */}
            {result.endpoint && (
              <button
                className="btn-secondary"
                onClick={async () => {
                  try {
                    const res = await fetch(result.endpoint, { credentials: "include" });
                    const body = await res.json();
                    if (res.ok && body.ok !== false) {
                      setError(null);
                      setResult({ ...result, _verified: true, _verify_status: res.status });
                    } else {
                      setError(`Verification failed: HTTP ${res.status}`);
                    }
                  } catch (err: any) {
                    setError(`Verification error: ${err.message}`);
                  }
                }}
              >
                Verify Endpoint
              </button>
            )}
          </div>

          {result._verified && (
            <div className="card-meta" style={{ marginTop: 8 }}>
              <span className="badge" data-status="active">✓ Verified</span>
              <span className="muted"> endpoint returned HTTP {result._verify_status}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
